import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormInput from '../YogaUI/FormInput';
import {
  IS_CHECKOUT_PAGE,
  IS_PRESUMABLY_EXISTING_CUSTOMER,
  MAX_NAME_LENGTH,
  globalWindow,
  MUI_MOBILE_BREAKPOINT,
} from '../../../../utils/constants';
import { phoneNumberFormatter } from '../SignInModal/utils';
import './styles.scss';
import { fireHeapCustomerCreateEvent, fireHeapCustomerLoginEvent } from '../../../../utils/heap-events';
import { ShippingCountryState } from '../../../../utils/shipping';
import { useDidUpdateEffect, usePromise } from '../../react-hooks';
import { isInGeotarget } from '../../../../utils/location';
import { SignInModalTab } from '../SignInModal/types';
import { getLoyaltyBlockedStatus } from '../../loyaltyBlockedStatus';
import { GWPandLoyaltyGeoSettings } from 'src/scripts/globals';
import { addRsvpTag, RSVP_COMMUNITY_TAG } from 'src/scripts/utils/rsvp-events';
import { Alert } from '@material-ui/lab';
import { useMediaQuery } from 'usehooks-ts';
import { InitialUrlSearchParams, isLocal } from 'src/scripts/utils/environment';
import InputMask from 'react-input-mask';
import { applyDiscountCode } from 'src/scripts/utils/autoCouponApplicator';
import { prependLocaleToPath, prependLocaleToUrl } from '../../../../utils/localization';
import FaceIdButton from '../FaceIdEnableConfirmModal/FaceIdButton';
import FACE_ID from 'src/scripts/utils/faceid-helpers';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { handleFormEvents, pushEventToGTMDataLayer } from 'src/scripts/utils/gtm-data-layer';
import Checkbox from '../YogaUI/Checkbox';
import { triggerMergeCartOnLogin } from 'src/scripts/utils/cart/cart-apis';
import ButtonWithProgress from '../YogaUI/ButtonWithProgress';
import sanitizeHtml from 'sanitize-html';
import { recordCustomEventToNewRelic } from '../../../../utils/helpers';
import { aloApiFetch } from 'src/scripts/utils/alo-api-fetch';
import { completeEmailOtpSignIn, startEmailOtpSignIn } from '../../../../utils/otp-auth';
import { useAccountModalConfig } from '../SignInModal/AccountModalConfigContext';

const defaultTab = IS_PRESUMABLY_EXISTING_CUSTOMER ? 'sign-in' : 'create-account';
type CustomerStateLookupStatus = 'activation-required' | 'non-activation' | 'error';
const ACTIVATION_EMAIL_SENT_COPY_PREFIX = 'Click the link in the email sent to';
const ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE = 'Unable to verify your account status right now. Please try again.';
type FormAlertType = 'success' | 'error';
type SignInMethod = 'password' | 'otp';
type OtpStep = 'request' | 'verify';
const OTP_CODE_LENGTH = 6;
const OTP_LOGIN_RETURN_URL_KEY = 'otp-login-return-url';
const emptyOtpDigits = () => Array.from({ length: OTP_CODE_LENGTH }, () => '');

const AuthForm = ({
  returnUrl,
  defaultTabOverride,
  isSignInModal = false,
  onTabSwitchCallback,
  trackingOptions,
  prePopulatedEmail: inputPrePopulatedEmail,
}: {
  returnUrl: string | undefined;
  defaultTabOverride?: SignInModalTab;
  isSignInModal?: boolean;
  onTabSwitchCallback?: (tab: SignInModalTab) => void;
  trackingOptions?: { location: string };
  prePopulatedEmail?: string;
}) => {
  const [formType, setFormType] = useState<SignInModalTab>(defaultTabOverride || defaultTab);
  const [recoveryPrefillEmail, setRecoveryPrefillEmail] = useState<string | null>(null);
  const [isCheckingCustomerState, setIsCheckingCustomerState] = useState(false);
  const prePopulatedEmail = recoveryPrefillEmail || FACE_ID.prePopulateFaceIDEmail(formType) || inputPrePopulatedEmail;

  const [customerEmailState, setCustomerEmailState] = useState(!!prePopulatedEmail || false);
  const [customerPasswordState, setCustomerPasswordState] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumberError, setPhoneNumberErrorState] = useState(false);
  const [birthdayError, setBirthdayErrorState] = useState({ shouldShowErrorUI: false, hasValidValue: true });
  const [resetPasswordEmail, setResetPasswordEmail] = useState('your email');
  const [heroVisibility, setHeroVisibility] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const phoneNumberTooltipMessage = 'Enables faster account lookup in-store.';
  const activeFormRef = useRef<HTMLFormElement>(null);

  const { data: isInUsGeoInfo, isLoading } = usePromise(() => isInGeotarget({ countries: ['US'] }));
  const isInUS = ShippingCountryState.countryCode ? ShippingCountryState.countryCode === 'US' : isInUsGeoInfo;

  const [formState, setFormState] = useState({
    FirstName: false,
    LastName: false,
    Email: prePopulatedEmail || false,
    CreatePassword: false,
    RecoverEmail: prePopulatedEmail || false,
  });

  const { accountModalSettings, loginPageSettings } = useAccountModalConfig();
  const {
    shouldEnableOtpSignIn,
    showNewsLetterCheckbox,
    emailOtpVerifyTitle,
    emailOtpUseCodeCta,
    emailOtpUsePasswordCta,
    shouldShowBirthdayInput,
    birthdayInputLabel,
    createAccountHeroImage,
    signInHeroImage,
    createAccountCta,
    signInCta,
    autoApplyDiscountCode,
  } = accountModalSettings;

  const defaultSignInMethod: SignInMethod = 'password';
  const [signInMethod, setSignInMethod] = useState<SignInMethod>(defaultSignInMethod);
  const [otpStep, setOtpStep] = useState<OtpStep>('request');
  const [otpEmailState, setOtpEmailState] = useState(!!prePopulatedEmail || false);
  const [otpDigits, setOtpDigits] = useState<string[]>(emptyOtpDigits);
  const [otpEmailAddress, setOtpEmailAddress] = useState(prePopulatedEmail || '');
  const [otpSentToEmail, setOtpSentToEmail] = useState(prePopulatedEmail || '');
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const otpHelperStatusContent = useMemo<React.ReactNode | null>(() => {
    if (otpStep !== 'verify') {
      return null;
    }

    if (isVerifyingOtp) {
      return 'Verifying code...';
    }

    if (isSendingOtp) {
      return 'Sending new code...';
    }

    if (otpResendCountdown > 0) {
      const otpResendUnitCopy = !isInUS || otpResendCountdown !== 1 ? 'seconds' : 'second';

      return (
        <>
          You can resend a new code in <span className="otp-helper-count notranslate">{otpResendCountdown}</span>{' '}
          <span>{otpResendUnitCopy}</span>
        </>
      );
    }

    return null;
  }, [isInUS, isSendingOtp, isVerifyingOtp, otpResendCountdown, otpStep]);

  const discountCodeToApplyOnSignIn = isLoading ? '' : autoApplyDiscountCode;

  const _returnUrl = useMemo(() => {
    const urlParams = InitialUrlSearchParams;
    const checkoutUrl = urlParams.get('checkout_url') || (IS_CHECKOUT_PAGE && window.location.href);

    if (checkoutUrl) {
      return checkoutUrl;
    } else {
      let urlObject: URL;
      try {
        urlObject = parseUrl(returnUrl || window.location.href);
      } catch (err) {
        console.error(err);
        urlObject = parseUrl(window.location.href);
      }

      urlObject.searchParams.set('auth_success', '1');
      urlObject.searchParams.set('is_modal', isSignInModal.toString());

      // ? [REVISIT] Can this be replaced with the more efficient 'applyDiscountCode'?
      // - only question/problem is that social login we don't have a hook into & we still need to generate the URL
      const finalUrl =
        discountCodeToApplyOnSignIn && discountCodeToApplyOnSignIn !== ''
          ? prependLocaleToPath(
              `/discount/${discountCodeToApplyOnSignIn}?redirect=${encodeURIComponent(
                prependLocaleToPath(urlObject.pathname)
              )}&${urlObject.searchParams}`
            )
          : prependLocaleToUrl(urlObject.toString());

      return finalUrl;
    }
  }, [returnUrl, isSignInModal, discountCodeToApplyOnSignIn]);

  useEffect(() => {
    onTabSwitchCallback && onTabSwitchCallback(formType);
  }, [formType, onTabSwitchCallback]);

  useEffect(() => {
    const originalReturnUrl = window.sessionStorage.getItem('social-login-return-url');
    window.sessionStorage.setItem('social-login-return-url', _returnUrl + '&auth_event=login&social_login=true');
    return () => {
      if (originalReturnUrl) {
        window.sessionStorage.setItem(
          'social-login-return-url',
          // ? Unrelated to this PR, but how does this work, we are adding `?` w/o checking if it already exists
          originalReturnUrl + '?auth_success=1&auth_event=login&social_login=true'
        );
      } else {
        window.sessionStorage.removeItem('social-login-return-url');
      }
    };
  }, [_returnUrl]);

  useEffect(() => {
    if (formType === 'create-account' || formType === 'sign-in') {
      const getUserStatus = async () => {
        const loyaltySettings = await GWPandLoyaltyGeoSettings();
        const isLoyaltyEnabled =
          loyaltySettings.shouldRenderLoyaltyForDomestic || loyaltySettings.shouldRenderLoyaltyWidgetForINTL;
        const isBlocked = await getLoyaltyBlockedStatus();
        const shouldRenderLoyalty = !isBlocked && isLoyaltyEnabled;
        setHeroVisibility(shouldRenderLoyalty);
      };
      getUserStatus();
    } else {
      setHeroVisibility(false);
    }
  }, [formType]);

  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT);
  const loginPageSignInHeroImage = isMobile
    ? loginPageSettings?.signInHeroMobile
    : loginPageSettings?.signInHeroDesktop;
  const loginPageSignUpHeroImage = isMobile
    ? loginPageSettings?.signUpHeroMobile
    : loginPageSettings?.signUpHeroDesktop;

  const heroImageUrl = useMemo(() => {
    if (formType === 'sign-in') {
      return isSignInModal ? signInHeroImage : loginPageSignInHeroImage;
    } else if (formType === 'create-account') {
      return isSignInModal ? createAccountHeroImage : loginPageSignUpHeroImage;
    }
  }, [
    formType,
    createAccountHeroImage,
    signInHeroImage,
    isSignInModal,
    loginPageSignInHeroImage,
    loginPageSignUpHeroImage,
  ]);

  const resetOtpState = useCallback(
    (nextEmail = prePopulatedEmail || '') => {
      setOtpStep('request');
      setOtpDigits(emptyOtpDigits());
      setOtpSentToEmail(nextEmail);
      setOtpEmailAddress(nextEmail);
      setOtpResendCountdown(0);
      setIsSendingOtp(false);
      setIsVerifyingOtp(false);
      setOtpEmailState(Boolean(nextEmail));
    },
    [prePopulatedEmail]
  );

  useEffect(() => {
    if (!shouldEnableOtpSignIn && signInMethod === 'otp') {
      setSignInMethod('password');
      resetOtpState();
    }
  }, [shouldEnableOtpSignIn, signInMethod, resetOtpState]);

  useEffect(() => {
    if (formType !== 'sign-in') {
      setSignInMethod(defaultSignInMethod);
      resetOtpState();
    }
  }, [formType, defaultSignInMethod, resetOtpState]);

  useEffect(() => {
    if (otpResendCountdown <= 0) return;
    const timer = window.setInterval(() => {
      setOtpResendCountdown((seconds) => (seconds > 1 ? seconds - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [otpResendCountdown]);

  useEffect(() => {
    if (otpStep !== 'verify') return;
    const timer = window.setTimeout(() => {
      otpInputRefs.current[0]?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [otpStep]);

  const routeToActivateAccount = useCallback((email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    setRecoveryPrefillEmail(normalizedEmail || email);
    setFormState((prevState) => ({
      ...prevState,
      FirstName: false,
      LastName: false,
      Email: Boolean(normalizedEmail),
      CreatePassword: false,
    }));
    setFormType('activate-account');
  }, []);

  const buildPostLoginDestination = useCallback(
    (authMode: 'password' | 'otp') => {
      if (IS_CHECKOUT_PAGE) {
        return _returnUrl;
      }

      const authModeParam = authMode === 'otp' ? '&auth_mode=otp' : '';
      return `${_returnUrl}${_returnUrl.includes('?') ? '&' : '?'}auth_event=login${authModeParam}`;
    },
    [_returnUrl]
  );

  const buildOtpAuthenticatedLandingUrl = useCallback(() => {
    const queryParams = new URLSearchParams({
      auth_success: '1',
      auth_event: 'login',
      auth_mode: 'otp',
      is_modal: isSignInModal.toString(),
    });
    return prependLocaleToPath(`/account?${queryParams.toString()}`);
  }, [isSignInModal]);

  // ? of note: these `Submittable` variables are unreliable since the forms/inputs are uncontrolled inputs
  // https://react.dev/learn/sharing-state-between-components#controlled-and-uncontrolled-components
  const isLoginFormSubmittable = useMemo(() => {
    return customerEmailState && customerPasswordState;
  }, [customerEmailState, customerPasswordState]);

  const isOtpRequestSubmittable = useMemo(() => {
    return otpEmailState;
  }, [otpEmailState]);

  const isOtpVerifySubmittable = useMemo(() => {
    return otpDigits.every((digit) => digit !== '') && otpSentToEmail.length > 0;
  }, [otpDigits, otpSentToEmail]);

  const isAccountFormSubmittable = useMemo(() => {
    return (
      formState.FirstName &&
      formState.LastName &&
      formState.Email &&
      formState.CreatePassword &&
      birthdayError.hasValidValue
    );
  }, [formState.FirstName, formState.LastName, formState.Email, formState.CreatePassword, birthdayError.hasValidValue]);

  const isResetPasswordSubmittable = useMemo(() => {
    return formState.RecoverEmail;
  }, [formState.RecoverEmail]);
  const isActivateAccountForm = formType === 'activate-account';

  const FormTypeSwitch = (
    <div className="auth-form-tabs" style={{ cursor: 'pointer', borderBottom: '1px solid var(--alto)' }}>
      <div
        className={`auth-form-tab h6 bold ${formType === 'create-account' ? 'active' : ''}`}
        onClick={() => setFormType('create-account')}
      >
        Join Now
      </div>
      <div
        className={`auth-form-tab h6 bold ${formType === 'sign-in' ? 'active' : ''}`}
        onClick={() => setFormType('sign-in')}
      >
        Sign In
      </div>
    </div>
  );

  const heroBlock = heroImageUrl ? (
    <img
      className="account__hero"
      src={heroImageUrl}
      alt={formType === 'sign-in' ? 'Sign In Hero Image' : 'Sign Up Hero Image'}
    />
  ) : null;

  const [formError, setFormError] = useState<FormError | null>(null);

  const checkCustomerStateFromAloApi = async (
    email: string
  ): Promise<{
    status: CustomerStateLookupStatus;
    errorMessage?: string;
  }> => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      return { status: 'non-activation' };
    }
    const startTime = performance.now();

    try {
      const response = await aloApiFetch(`/v1/account/status?email=${encodeURIComponent(normalizedEmail)}`);

      if (!response.ok) {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        recordCustomEventToNewRelic('account_status_lookup_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          statusCode: response.status,
          statusText: response.statusText,
          message: 'Alo API account status lookup returned non-OK response',
        });
        return {
          status: 'error',
          errorMessage: ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE,
        };
      }

      const payload = await response.json();
      if (payload?.requires_activation_view === true) {
        return { status: 'activation-required' };
      }

      return { status: 'non-activation' };
    } catch (error) {
      const endTime = performance.now();
      const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      recordCustomEventToNewRelic('account_status_lookup_error', {
        fetchDuration,
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        message: errorMessage,
      });
      console.error('Failed to lookup customer state via alo-api', error);
      return {
        status: 'error',
        errorMessage: ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE,
      };
    }
  };

  const submitResetPassword = async (event: React.FormEvent) => {
    if (isCheckingCustomerState) return;
    const startTime = performance.now();
    setFormError(null);
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get('email')?.toString() || '';

    setIsCheckingCustomerState(true);
    const customerState = await checkCustomerStateFromAloApi(email);

    if (customerState.status === 'activation-required') {
      routeToActivateAccount(email);
      setIsCheckingCustomerState(false);
      return;
    }

    if (customerState.status === 'error') {
      setFormError({
        type: 'recover',
        alertType: 'error',
        errorMessage: customerState.errorMessage || ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE,
      });
      setIsCheckingCustomerState(false);
      return;
    }

    // Continue with existing theme recover flow for non-disabled customers.
    fetch(prependLocaleToPath('/account/recover'), {
      method: 'POST',
      body: formData,
    })
      .then((res) => res.text())
      .then((response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        const errorText = getErrorMessagingFromShopify(response);
        if (errorText) {
          recordCustomEventToNewRelic('reset_password_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: errorText,
            response: JSON.stringify(response),
          });
          setFormError({ type: 'recover', alertType: 'error', errorMessage: errorText });
        } else {
          setFormType('reset-confirmation');
          setResetPasswordEmail(email);
          recordCustomEventToNewRelic('reset_password', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful Password Reset',
          });
          pushEventToGTMDataLayer({
            event: 'forget_password_form_submit',
            form: 'forget_password',
          });
        }
      })
      .catch((error) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        console.error(error);
        recordCustomEventToNewRelic('reset_password_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          message: error.message || 'Reset password fetch failed',
        });
      })
      .finally(() => {
        setIsCheckingCustomerState(false);
      });
  };

  const subscribeCustomerViaWiz = (email: string, tags?: string[]) => {
    if (!window?.wizUrl) return false;
    const startTime = performance.now();
    const wizEndpoint = `https://${window?.wizUrl}/offer-enroll`;
    const requestBody = {
      email: email,
      tags: tags || [],
      accepts_marketing: 1,
    };
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    };

    return fetch(wizEndpoint, options)
      .then((response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        if (!response.ok) {
          console.log('Offer enroll error: ', 'Network response was not OK');
          recordCustomEventToNewRelic('subscribe_customer_via_wiz_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Subscribe customer via WIZ failed: Network response was not OK - ' + JSON.stringify(response),
          });
          return false;
        }
        return response.json();
      })
      .then((data) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        if (data.status === 'success') {
          recordCustomEventToNewRelic('subscribe_customer_via_wiz', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful subscribe customer via WIZ',
          });
          return true;
        } else {
          recordCustomEventToNewRelic('subscribe_customer_via_wiz_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Subscribe customer via WIZ failed: ' + JSON.stringify(data),
          });
          return false;
        }
      });
  };

  const handleSuccessfulLogin = async ({
    email,
    acceptsMarketingOptIn,
    fetchDuration,
    authMode,
    otpRedirectUrl,
  }: {
    email: string;
    acceptsMarketingOptIn: boolean;
    fetchDuration: string;
    authMode: 'password' | 'otp';
    otpRedirectUrl?: string | null;
  }) => {
    const normalizedEmail = email.trim().toLowerCase();
    FACE_ID.incrementUserLoggedInCount(normalizedEmail);
    recordCustomEventToNewRelic('login', {
      fetchDuration,
      userAgent: window.navigator.userAgent,
      date: Date.now(),
      pathname: window.location.pathname,
      message: 'Successful Login',
      authMode,
    });
    fireHeapCustomerLoginEvent({ isSignInModal });
    if (acceptsMarketingOptIn && normalizedEmail.length > 0) {
      await subscribeCustomerViaWiz(normalizedEmail, ['offer-subscribed-via-login-form']);
    }
    const postLoginDestination = buildPostLoginDestination(authMode);

    if (otpRedirectUrl) {
      window.sessionStorage.setItem(OTP_LOGIN_RETURN_URL_KEY, postLoginDestination);
      window.location.href = otpRedirectUrl;
      return;
    }

    await triggerMergeCartOnLogin(true);
    if (discountCodeToApplyOnSignIn) {
      await applyDiscountCode(discountCodeToApplyOnSignIn);
    }

    window.location.href = postLoginDestination;
  };

  const login = async (formData: FormData) => {
    if (isAuthorizing) return; // Prevent duplicate submissions
    const startTime = performance.now();
    setIsAuthorizing(true);
    const acceptsMarketing = !!formData.get('customer[accepts_marketing]')?.toString();
    const email = String(formData.get('customer[email]') || '');
    setFormError(null);
    fetch(prependLocaleToPath('/account/login'), {
      method: 'post',
      body: formData,
    })
      .then((res) => res.text())
      .then(async (response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        const errorText = getErrorMessagingFromShopify(response);
        if (errorText) {
          setFormError({ type: 'login', alertType: 'error', errorMessage: errorText });
          recordCustomEventToNewRelic('login_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: errorText,
            response: JSON.stringify(response),
          });
        } else {
          await handleSuccessfulLogin({
            email,
            acceptsMarketingOptIn: acceptsMarketing,
            fetchDuration,
            authMode: 'password',
          });
        }
      })
      .catch((error) => {
        console.info({ error });
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        recordCustomEventToNewRelic('login_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          message: error.message || 'Login fetch failed',
        });
      })
      .finally(() => setIsAuthorizing(false)); //Reset value
  };

  const startOtpLogin = async (emailValue: string) => {
    if (isSendingOtp || isCheckingCustomerState) return;
    const email = emailValue.trim().toLowerCase();
    const startTime = performance.now();
    setIsSendingOtp(true);
    setFormError(null);

    try {
      setIsCheckingCustomerState(true);
      const customerState = await checkCustomerStateFromAloApi(email);
      if (customerState.status === 'activation-required') {
        routeToActivateAccount(email);
        return;
      }

      if (customerState.status === 'error') {
        throw new Error(customerState.errorMessage || ACCOUNT_STATUS_LOOKUP_ERROR_MESSAGE);
      }

      const otpResponse = await startEmailOtpSignIn(email);
      if (!otpResponse.success) {
        throw new Error(otpResponse.message || 'Unable to send one-time code.');
      }

      setOtpStep('verify');
      setOtpDigits(emptyOtpDigits());
      setOtpSentToEmail(email);
      setOtpEmailAddress(email);
      setOtpResendCountdown(otpResponse.resendAvailableInSeconds ?? 30);
      handleFormEvents('login_start', 'email otp');

      const endTime = performance.now();
      const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
      recordCustomEventToNewRelic('login_otp_start', {
        fetchDuration,
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        message: 'OTP sent',
      });
    } catch (error) {
      const endTime = performance.now();
      const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
      const errorMessage = error instanceof Error ? error.message : 'Unable to send one-time code.';
      setFormError({ type: 'login', alertType: 'error', errorMessage });
      recordCustomEventToNewRelic('login_otp_start_error', {
        fetchDuration,
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        message: errorMessage,
      });
    } finally {
      setIsCheckingCustomerState(false);
      setIsSendingOtp(false);
    }
  };

  const completeOtpLogin = async (otpValue: string) => {
    if (isVerifyingOtp || !otpSentToEmail) return;
    const startTime = performance.now();
    setIsVerifyingOtp(true);
    setFormError(null);

    try {
      const otpResponse = await completeEmailOtpSignIn(
        otpSentToEmail,
        otpValue.trim(),
        buildOtpAuthenticatedLandingUrl()
      );
      if (!otpResponse.success) {
        throw new Error(otpResponse.message || 'Invalid one-time code.');
      }

      const endTime = performance.now();
      const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
      recordCustomEventToNewRelic('login_otp_complete', {
        fetchDuration,
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        message: 'Successful OTP Login',
      });
      await handleSuccessfulLogin({
        email: otpSentToEmail,
        acceptsMarketingOptIn: acceptsMarketing,
        fetchDuration,
        authMode: 'otp',
        otpRedirectUrl: otpResponse.redirectUrl,
      });
    } catch (error) {
      const endTime = performance.now();
      const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
      const errorMessage = error instanceof Error ? error.message : 'Unable to verify one-time code.';
      setOtpDigits(emptyOtpDigits());
      window.setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 0);
      setFormError({ type: 'login', alertType: 'error', errorMessage });
      recordCustomEventToNewRelic('login_otp_complete_error', {
        fetchDuration,
        userAgent: window.navigator.userAgent,
        date: Date.now(),
        pathname: window.location.pathname,
        message: errorMessage,
      });
      // Keep the OTP loading state on success until navigation takes over; otherwise
      // the spinner clears before the browser leaves for the multipass redirect.
      setIsVerifyingOtp(false);
    }
  };

  const createAccount = async (formData: FormData) => {
    if (isAuthorizing) return; // Prevent duplicate submissions
    const startTime = performance.now();
    setIsAuthorizing(true);
    setFormError(null);
    // Attach customer tags for signup tracking
    try {
      const tagsToAdd: string[] = [];
      // Add hostname tag
      const hostname = window.location.hostname || '';
      if (hostname) {
        const normalized = hostname.toLowerCase().replace(/^www\./, '');
        tagsToAdd.push(normalized);
      }

      // Add RSVP tag if user signed up directly from RSVP modal on events page
      if (addRsvpTag()) {
        tagsToAdd.push(RSVP_COMMUNITY_TAG);
      }

      // Combine with existing tags and set once
      if (tagsToAdd.length > 0) {
        const existingTags = formData.get('customer[tags]')?.toString();
        const allTags = [existingTags, ...tagsToAdd].filter(Boolean).join(',');
        formData.set('customer[tags]', allTags);
      }
    } catch (err) {
      console.warn('Failed to attach customer tags:', err);
    }
    await fetch(prependLocaleToPath(`/account`), {
      method: 'post',
      body: formData,
    })
      .then((res) => res.text())
      .then(async (response) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        const errorText = getErrorMessagingFromShopify(response);
        const submittedEmail = String(formData.get('customer[email]') || '')
          .trim()
          .toLowerCase();
        const shouldShowSuccess = Boolean(
          errorText && submittedEmail && errorText.toLowerCase().includes(submittedEmail)
        );
        if (errorText) {
          const activationSuccessCopy = `${ACTIVATION_EMAIL_SENT_COPY_PREFIX} ${submittedEmail} to complete account activation.`;
          const signInActivationSuccessCopy = `Welcome back! ${activationSuccessCopy}`;
          setFormError({
            type: 'create',
            alertType: shouldShowSuccess ? 'success' : 'error',
            errorMessage: shouldShowSuccess
              ? isActivateAccountForm
                ? activationSuccessCopy
                : signInActivationSuccessCopy
              : errorText,
          });
          recordCustomEventToNewRelic('create_account_error', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: errorText,
            response: JSON.stringify(response),
          });
        } else {
          // redirect to return URL + heap tracking
          const customerPhone = formData.getAll('customer[note][phonenumber]')[0]?.toString();
          FACE_ID.incrementUserLoggedInCount(String(formData.get('customer[email]') || '').toLowerCase());
          recordCustomEventToNewRelic('create_account', {
            fetchDuration,
            userAgent: window.navigator.userAgent,
            date: Date.now(),
            pathname: window.location.pathname,
            message: 'Successful Sign Up',
          });
          // Can we count 3 characters as a valid phone number???
          const hasProvidedPhoneNumber = Boolean(customerPhone && customerPhone.length > 3);
          fireHeapCustomerCreateEvent({ hasProvidedPhoneNumber, isSignInModal });
          await triggerMergeCartOnLogin(true);
          if (discountCodeToApplyOnSignIn) await applyDiscountCode(discountCodeToApplyOnSignIn);
          if (IS_CHECKOUT_PAGE) {
            window.location.href = _returnUrl;
          } else {
            window.location.href = _returnUrl + '&auth_event=sign_up';
          }
        }
      })
      .catch((error) => {
        const endTime = performance.now();
        const fetchDuration = (endTime - startTime).toFixed(2).toString() + ' milliseconds';
        console.info({ error });
        recordCustomEventToNewRelic('create_account_error', {
          fetchDuration,
          userAgent: window.navigator.userAgent,
          date: Date.now(),
          pathname: window.location.pathname,
          message: error.message || 'Create Account fetch failed',
        });
      })
      .finally(() => setIsAuthorizing(false)); //Reset value
  };

  useDidUpdateEffect(() => {
    setFormError(null);
  }, [formType]);

  const [acceptsMarketing, setAcceptsMarketing] = useState(true);
  const handleRecoverAlertClick = useCallback(() => {
    setFormType('recovery');
  }, []);

  if (isLoading) return null;

  // -- note with the addition of otp we adjust the workaround introduced in PR#2410 which was concerned w/ checkout disabling submit buttons but this no longer runs from checkout so this should be safe, but we should keep an eye on it
  // Keep the submit-button reset key tied to form/error state, not live form values,
  // so countdown and input updates do not remount the button unnecessarily.
  const formKey = `${formType}-${formError?.type || 'none'}-${formError?.errorMessage || 'ready'}`;

  const validateBirthdayInput = (value: string, isOutOfFocus = false) => {
    const pattern = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])$/;
    if (value === '__/__' || value === '') {
      setBirthdayErrorState({ shouldShowErrorUI: false, hasValidValue: true });
    } else if (value.includes('_')) {
      setBirthdayErrorState({ hasValidValue: false, shouldShowErrorUI: isOutOfFocus ? true : false });
    } else {
      setBirthdayErrorState({ shouldShowErrorUI: !pattern.test(value), hasValidValue: pattern.test(value) });
    }
  };

  const loginEmailFormEvent = () => handleFormEvents('login_start', 'email');
  const signUpEmailFormEvent = () => handleFormEvents('sign_up_start', 'email');
  const switchSignInMethod = (method: SignInMethod) => {
    setSignInMethod(method);
    setFormError(null);
    resetOtpState(otpEmailAddress || prePopulatedEmail || '');
  };
  const returnToOtpRequestStep = () => {
    setFormError(null);
    resetOtpState(otpSentToEmail || otpEmailAddress || prePopulatedEmail || '');
  };
  const focusOtpInput = (index: number) => {
    otpInputRefs.current[index]?.focus();
  };
  const maybeSubmitOtpCode = (digits: string[]) => {
    if (digits.every((digit) => digit !== '') && !isVerifyingOtp) {
      handleFormEvents('login_start', 'email otp');
      completeOtpLogin(digits.join(''));
      return true;
    }
    return false;
  };
  const handleOtpDigitChange = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, '');
    const nextDigits = [...otpDigits];

    if (!nextValue) {
      nextDigits[index] = '';
      setOtpDigits(nextDigits);
      if (formError !== null) {
        setFormError(null);
      }
      return;
    }

    const digitsToApply = nextValue.slice(0, OTP_CODE_LENGTH - index).split('');
    digitsToApply.forEach((digit, digitOffset) => {
      nextDigits[index + digitOffset] = digit;
    });

    setOtpDigits(nextDigits);
    if (formError !== null) {
      setFormError(null);
    }

    if (maybeSubmitOtpCode(nextDigits)) {
      return;
    }

    const nextFocusIndex = Math.min(index + digitsToApply.length, OTP_CODE_LENGTH - 1);
    focusOtpInput(nextFocusIndex);
  };
  const handleOtpDigitPaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedDigits) {
      return;
    }

    event.preventDefault();

    const nextDigits = [...otpDigits];
    const digitsToApply = pastedDigits.slice(0, OTP_CODE_LENGTH - index).split('');
    digitsToApply.forEach((digit, digitOffset) => {
      nextDigits[index + digitOffset] = digit;
    });

    setOtpDigits(nextDigits);
    if (formError !== null) {
      setFormError(null);
    }

    if (maybeSubmitOtpCode(nextDigits)) {
      return;
    }

    const nextFocusIndex = Math.min(index + digitsToApply.length, OTP_CODE_LENGTH - 1);
    focusOtpInput(nextFocusIndex);
  };
  const handleOtpDigitKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      event.preventDefault();
      const nextDigits = [...otpDigits];
      nextDigits[index - 1] = '';
      setOtpDigits(nextDigits);
      if (formError !== null) {
        setFormError(null);
      }
      focusOtpInput(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusOtpInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < OTP_CODE_LENGTH - 1) {
      event.preventDefault();
      focusOtpInput(index + 1);
    }
  };
  const otpResendCta = 'Send new code';
  const shouldShowOtpNewsletterCheckbox = showNewsLetterCheckbox && (!shouldEnableOtpSignIn || otpStep !== 'verify');

  const nameInput = (
    <div className="row no-gutters" style={{ gap: 'var(--spacing-sm)' }}>
      <div className="col">
        <div className="form-group form-group-placeholder">
          <FormInput
            options={{
              id: 'FirstName',
              wrapperClasses: '',
              type: 'text',
              required: true,
              errorText: 'Required',
              label: 'First Name',
              name: 'customer[first_name]',
              pattern: '^(?!\\s*$).+',
              defaultValue: '',
              handleFormState: (isValid) => {
                setFormState({ ...formState, FirstName: isValid });
              },
              maxLength: MAX_NAME_LENGTH,
              autocomplete: 'given-name',
              onChange: signUpEmailFormEvent,
            }}
          />
        </div>
      </div>
      <div className="col">
        <div className="form-group form-group-placeholder">
          <FormInput
            options={{
              id: 'LastName',
              wrapperClasses: '',
              type: 'text',
              required: true,
              errorText: 'Required',
              label: 'Last Name',
              name: 'customer[last_name]',
              pattern: '^(?!\\s*$).+',
              defaultValue: '',
              handleFormState: (isValid) => {
                setFormState({ ...formState, LastName: isValid });
              },
              maxLength: MAX_NAME_LENGTH,
              autocomplete: 'family-name',
              onChange: signUpEmailFormEvent,
            }}
          />
        </div>
      </div>
    </div>
  );

  const emailInput = (
    <div className="form-group form-group-placeholder">
      <FormInput
        options={{
          id: 'Email',
          wrapperClasses: '',
          type: 'email',
          required: true,
          errorText: 'Please enter a valid email',
          label: 'Email',
          pattern: globalWindow?.emailRegex,
          defaultValue: prePopulatedEmail,
          name: 'customer[email]',
          handleFormState: (isValid) => {
            if (formError !== null) {
              setFormError(null);
            }
            setFormState({ ...formState, Email: isValid });
          },
          onChange: signUpEmailFormEvent,
          autocomplete: 'email',
        }}
      />
    </div>
  );

  return (
    <>
      {heroVisibility ? heroBlock : null}
      <div className="auth-form-wrapper">
        {formType === 'sign-in' ? (
          <>
            {FormTypeSwitch}
            {signInMethod === 'otp' ? (
              <form
                key={`otp_sign_in_${otpStep}`}
                ref={activeFormRef}
                id="otp_sign_in"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (otpStep === 'request') {
                    startOtpLogin(otpEmailAddress);
                    return;
                  }

                  if (isOtpVerifySubmittable) {
                    completeOtpLogin(otpDigits.join(''));
                  }
                }}
              >
                <div style={{ marginTop: '25px' }}></div>
                <div className="form-group" data-testid="otp-login-form">
                  {otpStep === 'request' ? (
                    <div className="form-group form-group-placeholder">
                      <FormInput
                        options={{
                          id: 'OtpCustomerEmail',
                          wrapperClasses: '',
                          type: 'email',
                          required: true,
                          errorText: 'Please enter a valid email',
                          label: 'Email',
                          pattern: globalWindow?.emailRegex,
                          name: 'otp[email]',
                          defaultValue: otpEmailAddress || prePopulatedEmail,
                          handleFormState: (isValid) => {
                            if (formError !== null) {
                              setFormError(null);
                            }
                            setOtpEmailState(isValid);
                          },
                          onChange: (event) => {
                            loginEmailFormEvent();
                            setOtpEmailAddress(event.currentTarget.value);
                          },
                          autocomplete: 'email',
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="otp-email-summary">
                        <p className="otp-email-summary-label">Email</p>
                        <div className="otp-email-summary-row">
                          <p className="otp-email-summary-value">{otpSentToEmail}</p>
                          <button
                            type="button"
                            className="otp-action-link otp-change-email-button"
                            onClick={returnToOtpRequestStep}
                          >
                            Change
                          </button>
                        </div>
                      </div>
                      <div className="otp-verify-section">
                        <p className="otp-verify-title">{emailOtpVerifyTitle}</p>
                        <div className="otp-code-input-row">
                          {otpDigits.map((digit, index) => (
                            <input
                              key={`otp-digit-${index}`}
                              ref={(input) => {
                                otpInputRefs.current[index] = input;
                              }}
                              className="otp-code-input"
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              autoComplete={index === 0 ? 'one-time-code' : 'off'}
                              aria-label={`Verification code digit ${index + 1}`}
                              maxLength={OTP_CODE_LENGTH}
                              value={digit}
                              onChange={(inputEvent) => handleOtpDigitChange(index, inputEvent.currentTarget.value)}
                              onPaste={(pasteEvent) => handleOtpDigitPaste(index, pasteEvent)}
                              onKeyDown={(keyEvent) => handleOtpDigitKeyDown(index, keyEvent)}
                              onFocus={(focusEvent) => focusEvent.currentTarget.select()}
                              disabled={isVerifyingOtp}
                            />
                          ))}
                        </div>
                      </div>
                      <input type="hidden" name="otp[code]" value={otpDigits.join('')} />
                    </>
                  )}
                  {formError && formError.type === 'login' && formError.errorMessage ? (
                    <AuthFormAlert
                      errorMessage={formError.errorMessage}
                      alertType={formError.alertType}
                      onRecoverClick={handleRecoverAlertClick}
                    ></AuthFormAlert>
                  ) : null}
                </div>
                {otpStep === 'request' ? (
                  <ButtonWithProgress
                    options={{
                      type: 'submit',
                      showProgressAnimation: isSendingOtp,
                      cta: 'SEND CODE',
                      wrapperClasses: 'form-group',
                      buttonClasses: `button-primary btn-primary btn-block`,
                      disableShrink: true,
                      key: formKey,
                      disabled: !isOtpRequestSubmittable || isSendingOtp || isCheckingCustomerState,
                      testId: 'submit-button',
                    }}
                  />
                ) : null}
                {otpStep === 'verify' ? (
                  <>
                    <ButtonWithProgress
                      options={{
                        type: 'submit',
                        showProgressAnimation: isVerifyingOtp,
                        cta: 'SIGN IN',
                        wrapperClasses: 'form-group',
                        buttonClasses: `button-primary btn-primary btn-block`,
                        disableShrink: true,
                        key: `${formKey}-otp-verify`,
                        disabled: !isOtpVerifySubmittable || isVerifyingOtp,
                        testId: 'otp-verify-submit-button',
                      }}
                    />
                    <div className="otp-helper-actions">
                      {otpHelperStatusContent ? (
                        <p className="otp-helper-status" aria-live="polite">
                          {otpHelperStatusContent}
                        </p>
                      ) : (
                        <button
                          type="button"
                          className="otp-action-link"
                          disabled={isSendingOtp}
                          onClick={() => startOtpLogin(otpSentToEmail || otpEmailAddress)}
                        >
                          {otpResendCta}
                        </button>
                      )}
                    </div>
                  </>
                ) : null}
                {shouldShowOtpNewsletterCheckbox && (
                  <>
                    <Checkbox
                      options={{
                        id: 'SubscribeOtpLoginForm',
                        label: 'Email me with exclusive drops and offers',
                        checked: acceptsMarketing,
                        name: 'otp[accepts_marketing]',
                        onChange: (event) => {
                          setAcceptsMarketing(event.currentTarget.checked);
                        },
                      }}
                    />
                    <span className="label-add" style={{ textAlign: 'left', paddingTop: 8 }}>
                      By joining, you agree to our{' '}
                      <a href="/pages/terms-of-use/" target="_blank" className="terms-of-service-link">
                        Terms of Service
                      </a>{' '}
                      and to receive our email newsletter.
                    </span>
                  </>
                )}
              </form>
            ) : (
              <form
                key="customer_login"
                ref={activeFormRef}
                id="customer_login"
                // eslint-disable-next-line no-script-url
                action="javascript:void(0);"
                onSubmit={(e) => {
                  e.preventDefault();
                  try {
                    login(new FormData(e.currentTarget));
                  } catch (error) {
                    console.error('Error firing tracking events for Sign In', error);
                  }
                }}
              >
                <input type="hidden" name="form_type" value="customer_login" />
                <input type="hidden" name="utf8" value="✓" />
                {signInCta ? (
                  <p className="cta-title" dangerouslySetInnerHTML={{ __html: signInCta || '' }}></p>
                ) : (
                  <div style={{ marginTop: '25px' }}></div>
                )}
                <div className="form-group" data-testid="login-form">
                  <div className="form-group form-group-placeholder">
                    <FormInput
                      options={{
                        id: 'CustomerEmail',
                        wrapperClasses: '',
                        type: 'email',
                        required: true,
                        errorText: 'Please enter a valid email',
                        label: 'Email',
                        pattern: globalWindow?.emailRegex,
                        name: 'customer[email]',
                        defaultValue: prePopulatedEmail,
                        handleFormState: (isValid) => {
                          if (formError !== null) {
                            setFormError(null);
                          }
                          setCustomerEmailState(isValid);
                        },
                        onChange: loginEmailFormEvent,
                        autocomplete: 'email',
                      }}
                    />
                    <ErrorBoundary key={(!isLocal || new Date()).toString()}>
                      {FACE_ID.showFaceIdLoginButton() ? <FaceIdButton faceIdReturnUrl={_returnUrl} /> : null}
                    </ErrorBoundary>
                  </div>
                  <div className="form-group form-group-placeholder">
                    <FormInput
                      options={{
                        id: 'CustomerPassword',
                        wrapperClasses: '',
                        type: showPassword ? 'text' : 'password',
                        required: true,
                        errorText: 'Please enter your password',
                        label: 'Password',
                        pattern: '^(?!\\s*$).+',
                        name: 'customer[password]',
                        handleFormState: (isValid) => {
                          if (formError !== null) {
                            setFormError(null);
                          }
                          setCustomerPasswordState(isValid);
                        },
                        autocomplete: 'current-password',
                        onChange: loginEmailFormEvent,
                      }}
                    />
                    <div className="__password-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {!showPassword ? (
                        <span className="alo-eye-icon"></span>
                      ) : (
                        <span className="alo-eye-slash-icon"></span>
                      )}
                    </div>
                  </div>
                  {formError && formError.type === 'login' && formError.errorMessage ? (
                    <AuthFormAlert
                      errorMessage={formError.errorMessage}
                      alertType={formError.alertType}
                      onRecoverClick={handleRecoverAlertClick}
                    ></AuthFormAlert>
                  ) : null}
                </div>
                <div className="forgot-password-link">
                  <span
                    aria-label="Forgot password"
                    data-testid="forgot-password-link"
                    onClick={() => setFormType('recovery')}
                  >
                    Forgot Password?
                  </span>
                </div>

                <ButtonWithProgress
                  options={{
                    type: 'submit',
                    showProgressAnimation: isAuthorizing,
                    cta: 'Sign in',
                    wrapperClasses: 'form-group',
                    buttonClasses: `button-primary btn-primary btn-block`,
                    disableShrink: true,
                    key: formKey,
                    disabled: !isLoginFormSubmittable || isAuthorizing,
                    testId: 'submit-button',
                  }}
                />
                {shouldEnableOtpSignIn ? (
                  <button
                    type="button"
                    className="otp-switch-method-button button-secondary btn-secondary btn-block"
                    onClick={() => switchSignInMethod('otp')}
                  >
                    {emailOtpUseCodeCta}
                  </button>
                ) : null}
                {showNewsLetterCheckbox && (
                  <>
                    <Checkbox
                      options={{
                        id: 'SubscribeLoginForm',
                        label: 'Email me with exclusive drops and offers',
                        checked: acceptsMarketing,
                        name: 'customer[accepts_marketing]',
                        onChange: (event) => {
                          setAcceptsMarketing(event.currentTarget.checked);
                        },
                      }}
                    />
                    <span className="label-add" style={{ textAlign: 'left', paddingTop: 8 }}>
                      By joining, you agree to our{' '}
                      <a href="/pages/terms-of-use/" target="_blank" className="terms-of-service-link">
                        Terms of Service
                      </a>{' '}
                      and to receive our email newsletter.
                    </span>
                  </>
                )}
              </form>
            )}
            <div className="otp-alt-divider auth-social-divider" aria-hidden="true">
              <span className="otp-alt-divider-line"></span>
              <span className="otp-alt-divider-text">or</span>
              <span className="otp-alt-divider-line"></span>
            </div>
            {shouldEnableOtpSignIn && signInMethod === 'otp' ? (
              <button
                type="button"
                className="otp-action-link otp-switch-method"
                disabled={isVerifyingOtp || isSendingOtp}
                onClick={() => switchSignInMethod('password')}
              >
                {emailOtpUsePasswordCta}
              </button>
            ) : null}
            <OxiSocialLogin />
          </>
        ) : formType === 'create-account' || formType === 'activate-account' ? (
          <>
            {!isActivateAccountForm ? FormTypeSwitch : null}
            <form
              ref={activeFormRef}
              key={isActivateAccountForm ? 'activate_customer' : 'create_customer'}
              id={isActivateAccountForm ? 'activate_customer' : 'create_customer'}
              onSubmit={(e) => {
                e.preventDefault();
                try {
                  createAccount(new FormData(e.currentTarget));
                } catch (error) {
                  console.error('Error firing tracking events for Create Account', error);
                }
              }}
            >
              <input type="hidden" name="form_type" value="create_customer" />
              <input type="hidden" name="utf8" value="✓" />
              {isActivateAccountForm ? (
                <>
                  <h2 className="recovery-title">We Found Your Email On File</h2>
                  <p className="activate-account-subtitle">
                    Finish setting up your account to manage your orders and enjoy a faster checkout.
                  </p>
                </>
              ) : createAccountCta ? (
                <p className="cta-title" dangerouslySetInnerHTML={{ __html: createAccountCta || '' }}></p>
              ) : (
                <div style={{ marginTop: '25px' }}></div>
              )}
              <div className="form-group">
                {isActivateAccountForm ? emailInput : nameInput}
                {isActivateAccountForm ? nameInput : emailInput}

                <div className="form-group form-group-placeholder">
                  <FormInput
                    options={{
                      id: 'CreatePassword',
                      wrapperClasses: '',
                      type: showPassword ? 'text' : 'password',
                      required: true,
                      errorText: 'Must be at least 6 characters',
                      label: 'Password',
                      pattern: '^.{6,}$',
                      name: 'customer[password]',
                      handleFormState: (isValid) => {
                        if (formError !== null) {
                          setFormError(null);
                        }
                        setFormState({ ...formState, CreatePassword: isValid });
                      },
                      onChange: signUpEmailFormEvent,
                      autocomplete: 'new-password',
                    }}
                  />
                  <div className="__password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {!showPassword ? (
                      <span className="alo-eye-icon"></span>
                    ) : (
                      <span className="alo-eye-slash-icon"></span>
                    )}
                  </div>
                </div>
                {isInUS && !isActivateAccountForm ? (
                  <>
                    <div className="form-group form-group-placeholder">
                      <div className={`yoga-ui-text-input  ${phoneNumberError ? 'error-border' : ''}`}>
                        <input
                          type="tel"
                          name="customer[note][phonenumber]"
                          id="PhoneNumber"
                          className=" "
                          placeholder="Phone Number (optional)"
                          spellCheck="false"
                          autoComplete="tel"
                          autoCapitalize="off"
                          onChange={(e) => {
                            signUpEmailFormEvent();
                            phoneNumberFormatter(e);
                          }}
                          onBlur={(e) => {
                            const pattern = /^\(\d{3}\)\s\d{3}[- ]{1}\d{4}$/;
                            if (e.currentTarget.value === '') {
                              setPhoneNumberErrorState(false);
                            } else {
                              setPhoneNumberErrorState(!pattern.test(e.currentTarget.value));
                            }
                          }}
                          onFocus={(e) => {
                            setPhoneNumberErrorState(false);
                          }}
                        />
                        <label htmlFor="PhoneNumber" className="">
                          Phone Number
                        </label>
                        {phoneNumberError ? (
                          <span className="error error-input-form">Please enter a valid US number</span>
                        ) : null}
                        <MoreInfoTooltip text={phoneNumberTooltipMessage} />
                      </div>
                    </div>
                    {shouldShowBirthdayInput && (
                      <div className="form-group form-group-placeholder">
                        {birthdayInputLabel ? <p className="birthday-input-description">{birthdayInputLabel}</p> : null}
                        <div className={`yoga-ui-text-input  ${birthdayError ? 'error-border' : ''}`}>
                          <InputMask
                            mask="99/99"
                            name="customer[note][birthday]"
                            spellCheck={false}
                            id="Birthday"
                            placeholder="MM/DD (optional)"
                            inputMode="numeric"
                            onBlur={(e) => validateBirthdayInput(e.currentTarget.value, true)}
                            onChange={(e) => {
                              signUpEmailFormEvent();
                              validateBirthdayInput(e.currentTarget.value);
                            }}
                            onFocus={() => {
                              // Hide Error UI on input focus
                              setBirthdayErrorState({ ...birthdayError, shouldShowErrorUI: false });
                            }}
                          />
                          <label htmlFor="Birthday" className="">
                            Birthday
                          </label>
                          {birthdayError.shouldShowErrorUI ? (
                            <span className="error error-input-form">Please enter a valid date</span>
                          ) : null}
                          <MoreInfoTooltip text="Get a special Alo treat on your birthday!" />
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              {formError && formError.type === 'create' && formError.errorMessage ? (
                <AuthFormAlert
                  errorMessage={formError.errorMessage}
                  alertType={formError.alertType}
                  onRecoverClick={handleRecoverAlertClick}
                ></AuthFormAlert>
              ) : null}
              <ButtonWithProgress
                options={{
                  type: 'submit',
                  showProgressAnimation: isAuthorizing,
                  cta: isActivateAccountForm ? 'Activate Account' : 'Join Now',
                  wrapperClasses: 'form-group',
                  buttonClasses: `button-primary btn-primary btn-block`,
                  disableShrink: true,
                  key: formKey,
                  disabled: !isAccountFormSubmittable || isAuthorizing,
                }}
              />
            </form>
            {isActivateAccountForm ? (
              <p className="activate-account-help">
                Need help?{' '}
                <a href="/pages/contact-us" style={{ textDecoration: 'underline' }}>
                  Contact us
                </a>
              </p>
            ) : (
              <span className="label-add">
                By joining, you agree to our{' '}
                <a href="/pages/terms-of-use" target="_blank" className="terms-of-service-link">
                  Terms of Service
                </a>{' '}
                and to receive email and mobile marketing messages from ALO.
              </span>
            )}
          </>
        ) : formType === 'reset-confirmation' ? (
          <div className="reset-email-sent-confirmation">
            <h2>Reset Email Sent</h2>
            <p style={{ margin: '30px 15px' }}>
              If <span className="email GlobalLinkNoTx">{resetPasswordEmail}</span> is associated with a valid account,
              you will receive password reset instructions via email.
            </p>
            <p style={{ margin: '20px 0' }}>
              Having trouble?{' '}
              <a href="/pages/contact-us" style={{ textDecoration: 'underline' }}>
                Contact us
              </a>
            </p>
          </div>
        ) : (
          <>
            <form
              ref={activeFormRef}
              key="forgot_password"
              id="ModalRecoverPasswordForm"
              onSubmit={(event) => submitResetPassword(event)}
            >
              <input type="hidden" name="form_type" value="recover_customer_password" />
              <input type="hidden" name="utf8" value="✓" />

              <h2 className="recovery-title">Forgot Password?</h2>
              <p style={{ margin: '24px' }}>
                Please enter your email address to receive a link to reset your password.
              </p>
              <div className="form-group form-group-collapse">
                <div className="form-group form-group-placeholder">
                  <FormInput
                    options={{
                      id: 'RecoverEmail',
                      wrapperClasses: '',
                      type: 'email',
                      required: true,
                      errorText: 'Please enter a valid email',
                      label: 'Email',
                      pattern: globalWindow?.emailRegex,
                      defaultValue: prePopulatedEmail,
                      name: 'email',
                      handleFormState: (isValid) => {
                        if (formError !== null) {
                          setFormError(null);
                        }
                        setFormState({ ...formState, RecoverEmail: isValid });
                      },
                      onChange: () => {
                        handleFormEvents('forget_password_form_start');
                      },
                    }}
                  />
                </div>
              </div>
              {formError && formError.type === 'recover' && formError.errorMessage ? (
                <AuthFormAlert
                  errorMessage={formError.errorMessage}
                  alertType={formError.alertType}
                  onRecoverClick={handleRecoverAlertClick}
                ></AuthFormAlert>
              ) : null}
              <div className="form-group">
                <button
                  type="submit"
                  className="button-primary btn btn-primary btn-block"
                  value="Reset Password"
                  key={formKey}
                  disabled={!isResetPasswordSubmittable || isCheckingCustomerState}
                >
                  Reset Password
                </button>
              </div>
              <div>
                <p>
                  Having trouble?{' '}
                  <a href="/pages/contact-us" style={{ textDecoration: 'underline' }}>
                    Contact us
                  </a>
                </p>
                <p>
                  Don't have an account?{' '}
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setFormType('create-account')}
                  >
                    Join now
                  </span>
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default AuthForm;

const OxiSocialLogin = React.memo(() => {
  // if origin contains aloyoga.com redirect to aloyoga.com else alo.com
  const parenturl =
    window.location.origin === 'https://www.alo.com' ? 'https://www.alo.com' : 'https://www.aloyoga.com';
  return (
    <div className="login-item social-login">
      <h6 className="title h6 bold uppercase">One-Click Sign In</h6>
      <div className="form-group" style={{ marginTop: '0.5rem' }}>
        <div className="oxi-social-login">
          <iframe
            onClick={(e) => console.log(e)}
            onClickCapture={(e) => console.log(e)}
            id="social_login_frame"
            title="Social Login Frame"
            // ? [REVISIT] pass through
            // ? [REVISIT] rewrite this & test dynamic/anywhere returnTo with all 3 providers (which might force allow listed return urls)
            // src={`https://social-login.oxiapps.com/widget?site=alo-yoga.myshopify.com&vt=928382&parenturl=https://www.aloyoga.com/account/login?return_url=%2Faccount`}
            // ? strictly making use of the 'social-login-redirect' fallback/else case
            src={`https://social-login.oxiapps.com/widget?site=alo-yoga.myshopify.com&vt=928382&parenturl=${parenturl}/account/login`}
            scrolling="no"
          />
        </div>
      </div>
    </div>
  );
});

// -- our TS version isn't setup to be aware of URL.canParse
function newURL(url: string, base?: string | URL | undefined): URL | null {
  try {
    return new URL(url, base);
  } catch (error) {
    return null;
  }
}

function parseUrl(inputString: string): URL {
  let result: URL | null;
  result = newURL(inputString);
  if (result) return result; // base case
  result = newURL(inputString, window.location.origin);
  if (result) return result; // relative path

  throw new Error('Failed to parse redirect URL: ' + inputString);
}

const MoreInfoTooltip = ({ text }: { text: string }) => {
  return (
    <div className="more-info-tooltip">
      <div className="more-info-tooltip-icon">
        <img src="https://cdn.shopify.com/s/files/1/2185/2813/files/icon.svg?v=1675189680" alt="More info" />
      </div>
      <span className="tooltip-text">{text}</span>
    </div>
  );
};

const AuthFormAlert = React.memo(
  ({
    errorMessage,
    alertType,
    onRecoverClick,
  }: {
    errorMessage: string;
    alertType: FormAlertType;
    onRecoverClick: () => void;
  }) => {
    const errorRef = useRef<HTMLDivElement>(null);
    const containsResetLink = errorMessage.includes('reset your password');

    useEffect(() => {
      if (containsResetLink) {
        const link = errorRef.current?.querySelector('a[href*="recover"]');
        if (link) {
          const handleClick = (e: Event) => {
            e.preventDefault();
            onRecoverClick();
          };
          link.addEventListener('click', handleClick);
          return () => {
            link.removeEventListener('click', handleClick);
          };
        }
      }
    }, [containsResetLink, errorMessage, onRecoverClick]);

    return (
      <Alert severity={alertType} ref={errorRef} style={{ marginBottom: 20 }}>
        <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
      </Alert>
    );
  }
);

const getErrorMessagingFromShopify = (response: string) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(response, 'text/html');
    const errorDiv = doc.querySelector('div.errors li') || doc.querySelector('div.errors');

    if (!errorDiv) {
      return;
    }

    // decode HTML before sanitizing
    const ta = document.createElement('textarea');
    ta.innerHTML = errorDiv.innerHTML;

    return sanitizeHtml(ta.value);
  } catch (err) {
    console.error('Error parsing Shopify error response:', err);
    return 'An error occurred';
  }
};

interface FormError {
  type: 'create' | 'login' | 'recover';
  alertType: FormAlertType;
  errorMessage: string;
}
